import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import api, { formatErr } from "@/lib/api";

// Vitamin C readiness questionnaire — 4 conditional questions.
// Modeled on RetinoidAugmentation.jsx for visual + interaction consistency.
export default function VitaminCReadiness() {
  const nav = useNavigate();
  const [q1, setQ1] = useState("");
  const [q2, setQ2] = useState("");
  const [q3, setQ3] = useState("");
  const [q4, setQ4] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // Current visible step in the flow
  const step = result
    ? "result"
    : !q1 ? "q1"
    : q1 !== "every_evening" ? "block"
    : !q2 ? "q2"
    : q2 === "no" ? "block"
    : !q3 ? "q3"
    : q3 === "yes" ? "block"
    : !q4 ? "q4"
    : q4 === "yes" ? "block"
    : "submit";

  // Auto-submit once we reach the "submit" step
  const handleSubmit = async () => {
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/vitamin-c-readiness", { q1, q2, q3, q4 });
      setResult(data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };

  // When user finishes Q4 with "no", auto-submit (no extra click needed)
  useEffect(() => {
    if (step === "submit" && !result && !loading) {
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const blockMessage = q1 && q1 !== "every_evening"
    ? "Before adding in vitamin C, let's make sure you're using and tolerating your retinoid consistently on a nightly basis.\n\nOnce you've been using your retinoid every evening for at least 2 weeks, feel free to revisit this questionnaire and try again."
    : "If you are experiencing excessive dryness, peeling, redness, stinging, itching or burning, please consult with your health care provider.\n\nIn some mild cases, your skin may need a bit more time to adapt to your retinoid. In other cases, you may need to step down your retinoid potency or increase skin barrier support.";

  const Pill = ({ active, onClick, children, ...rest }) => (
    <button type="button" onClick={onClick} {...rest}
      className={`aa-pill px-5 py-2 mr-2 mb-2 ${active ? "bg-[var(--aa-text)] text-[var(--aa-bg)] border-[var(--aa-text)]" : "hover:bg-[var(--aa-elevated)]"}`}>
      {children}
    </button>
  );

  return (
    <div className="App" data-testid="page-vitamin-c-readiness">
      <NavBar />
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-14">
        <div className="aa-eyebrow mb-3">Vitamin C readiness</div>
        <h1 className="font-display text-4xl lg:text-5xl leading-tight">
          Ready to begin <span className="serif-italic">vitamin C</span>?
        </h1>
        <div className="aa-card p-4 mt-6 verdict-yellow" data-testid="vitc-stop">
          <strong>STOP:</strong> Enter only after you have used your retinoid every evening for at least 2 weeks. Your skin should feel calm, comfortable and hydrated on your current routine.
        </div>

        {/* Q1 */}
        {(step === "q1" || ["q2","q3","q4","submit","result"].includes(step)) && (
          <Question num={1} text="How often are you using your retinoid?" testid="vitc-q1">
            <Pill data-testid="vitc-q1-a" active={q1==="every_evening"} onClick={() => setQ1("every_evening")}>Every evening</Pill>
            <Pill data-testid="vitc-q1-b" active={q1==="every_other"} onClick={() => setQ1("every_other")}>Every other evening</Pill>
            <Pill data-testid="vitc-q1-c" active={q1==="less"} onClick={() => setQ1("less")}>Less than that</Pill>
          </Question>
        )}

        {/* Q2 */}
        {(step === "q2" || ["q3","q4","submit","result"].includes(step)) && (
          <Question num={2} text="Do your face, neck and chest feel hydrated (not dry or tight)?" testid="vitc-q2">
            <Pill data-testid="vitc-q2-a" active={q2==="yes"} onClick={() => setQ2("yes")}>Yes</Pill>
            <Pill data-testid="vitc-q2-b" active={q2==="most"} onClick={() => setQ2("most")}>Most of the time</Pill>
            <Pill data-testid="vitc-q2-c" active={q2==="no"} onClick={() => setQ2("no")}>No</Pill>
          </Question>
        )}

        {/* Q3 */}
        {(step === "q3" || ["q4","submit","result"].includes(step)) && (
          <Question num={3} text="Do you have any peeling or flaking on your face, neck or chest?" testid="vitc-q3">
            <Pill data-testid="vitc-q3-a" active={q3==="yes"} onClick={() => setQ3("yes")}>Yes</Pill>
            <Pill data-testid="vitc-q3-b" active={q3==="no"} onClick={() => setQ3("no")}>No</Pill>
          </Question>
        )}

        {/* Q4 */}
        {(step === "q4" || ["submit","result"].includes(step)) && (
          <Question num={4} text="Are you experiencing any irritation — redness in response to products, stinging, itching, or burning?" testid="vitc-q4">
            <Pill data-testid="vitc-q4-a" active={q4==="yes"} onClick={() => setQ4("yes")}>Yes</Pill>
            <Pill data-testid="vitc-q4-b" active={q4==="no"} onClick={() => setQ4("no")}>No</Pill>
          </Question>
        )}

        {step === "block" && (
          <div className="aa-card p-7 mt-7 fade-in" data-testid="vitc-block-msg">
            <div className="aa-eyebrow mb-3">Hold for now</div>
            <p className="text-[var(--aa-text-2)] whitespace-pre-line">{blockMessage}</p>
          </div>
        )}

        {step === "submit" && loading && (
          <div className="aa-eyebrow mt-7">Checking…</div>
        )}

        {err && <div className="text-sm text-[var(--aa-terra)] mt-4" data-testid="vitc-error">{err}</div>}

        {step === "result" && result?.ready && (
          <div className="aa-card p-7 mt-7 fade-in space-y-5" data-testid="vitc-result">
            <span className="aa-pill verdict-green">Ready for vitamin C</span>
            <h2 className="font-display text-2xl">Woo-hoo!</h2>
            <p className="text-[var(--aa-text-2)] whitespace-pre-line leading-relaxed">{result.message}</p>
            <div className="flex flex-wrap gap-3">
              <button data-testid="vitc-go-protocol" onClick={() => nav("/protocol")} className="aa-cta-primary">View my protocol →</button>
              <button data-testid="vitc-back" onClick={() => nav("/dashboard")} className="aa-cta-secondary">← Back to dashboard</button>
            </div>
          </div>
        )}

        {step === "result" && result && !result.ready && (
          <div className="aa-card p-7 mt-7 fade-in" data-testid="vitc-block-result">
            <div className="aa-eyebrow mb-3">Hold for now</div>
            <p className="text-[var(--aa-text-2)] whitespace-pre-line leading-relaxed">{result.message}</p>
            <button data-testid="vitc-back-blocked" onClick={() => nav("/dashboard")} className="aa-cta-secondary mt-6">← Back to dashboard</button>
          </div>
        )}
      </section>
    </div>
  );
}

function Question({ num, text, children, testid }) {
  return (
    <div className="aa-card p-6 mt-6" data-testid={testid}>
      <div className="flex items-start gap-3">
        <span className="aa-eyebrow w-8 mt-1">{String(num).padStart(2, "0")}</span>
        <div className="flex-1">
          <div className="text-[var(--aa-text)] mb-3">{text}</div>
          <div className="flex flex-wrap">{children}</div>
        </div>
      </div>
    </div>
  );
}
